import {
  XOR,
  enumKeys,
  AtLeastOne,
  PredictableHashingBehavior,
  JavaishSet,
} from "../../common/types";
import { FullAsinWithAudits, RestrictedAsin } from "./model";
import { getMarketplaceId } from "../acs/acs-service";

type PermissableValue = string | Date;
export type QueryableObject = {
  [key: string]: PermissableValue | boolean | undefined | null | (() => string);
} & PredictableHashingBehavior;
type Obj = QueryableObject;

export interface Page {
  from: number;
  pageSize: number;
  totalResultCount?: number;
}

export interface Sort {
  sortAttributes: SortFacet[];
}

export interface SortFacet {
  attributeName: string;
  ascending: boolean;
}

export interface Aggregation {
  aggregationAttributes: AggregationFacet[];
}

export interface AggregationFacet {
  attributeName: string;
  aggregationType: string;
  totalAggregationCount: number;
}

export interface AggregationResult {
  [key: string]: BucketResult[];
}

export interface BucketResult {
  key: string;
  documentCount: number;
}

// Structured nested-aggregation payload for the pivot request. The
// query-string aggregation grammar (attr:terms:N / attr:min:0) does not
// compose a recursive tree cleanly, so the pivot request travels on a
// POST body whose shape mirrors the DACS Coral nested-aggregation model:
// a terms bucket at each row-group level, optionally wrapped in a
// `nested` scope (for the `validationDetailResult*.detail` entry points),
// with a per-level `filter` used to count sub-buckets like `result: pass`.
export interface NestedAggregationFilterSpec {
  readonly field: string;
  readonly value: string;
  /**
   * Response sub-aggregation key for the filter result. When unset,
   * DACS keys the result under `"filter"` by default. Distinct filters
   * attached to the same parent facet must use distinct names.
   */
  readonly name?: string;
}

export interface NestedAggregationFacet {
  readonly termsField: string;
  readonly nestedPath?: string;
  readonly size: number;
  readonly filter?: NestedAggregationFilterSpec;
  /**
   * Two or more sibling filter sub-aggregations on the same terms
   * bucket. Each entry MUST carry a distinct non-blank `name` so its
   * result is addressable in the response's sub-aggregation map. Mirrors
   * the DACS model's `NestedAggregationFacet.filters` list; use this
   * (rather than the singular `filter`) whenever a facet needs more than
   * one count, e.g. a `passCount` alongside an `applicableCount`.
   */
  readonly filters?: readonly NestedAggregationFilterSpec[];
  readonly subAggregations?: readonly NestedAggregationFacet[];
}

export interface ScalarAggregationResults {
  [attributeName: string]: number | null;
}

// Recursive nested-aggregation response envelope. Mirrors the DACS
// Coral model's `NestedAggregationResults` map: keys are sub-aggregation
// names from the request (termsField for terms sub-aggs, filter.name
// for filter sub-aggs), values carry either a `buckets` list (terms) or
// a scalar `documentCount` (filter), never both. The middleware
// forwards this shape unchanged from DACS to the client.
export interface NestedBucket {
  readonly key: string;
  readonly documentCount: number;
  readonly subAggregations?: NestedAggregationResults;
}

export interface NestedAggregationBucketList {
  readonly buckets?: readonly NestedBucket[];
  readonly documentCount?: number;
}

export interface NestedAggregationResults {
  readonly [name: string]: NestedAggregationBucketList;
}

enum CombinatorTypes {
  and,
  or,
  not,
}

enum ValueParentTypes {
  fuzzy,
  filter,
  range,
}

export interface QueryNode {
  type: string;
  toString: () => string;
  getSubQueries: () => QueryType[];
}

interface ToCoralable<CoralType> {
  toCoral: () => CoralType;
}

type CoralString = {
  stringValue: string;
};
type CoralDate = {
  dateValue: Date;
};
export type CoralFilterValue = {
  filterValue: CoralString;
};
type CoralFuzzyValue = {
  matchValue: string;
};
export type CoralRangeValue = {
  rangeValue: {
    inclusiveLowerBound: CoralDate;
    inclusiveUpperBound: CoralDate;
  };
};
type CoralQueryValue = CoralFilterValue | CoralFuzzyValue | CoralRangeValue;
export type CoralValueParentQuery = {
  attributeName: string;
  queryValue: CoralQueryValue;
};

// type CoralValueParentQuery = CoralQueryValue;

type CoralCombinator = {
  combinator: string;
  subQueries: (CoralCombinator | CoralValueParentQuery)[];
};

export interface ActionableQuery<T extends Obj> extends QueryNode {
  toSet(superset: JavaishSet<T>): JavaishSet<T>;
}

export type QueryType = ActionableQuery<QueryableObject> & ToCoralable<object>;

export class AllResults<T extends Obj>
  implements QueryNode, ActionableQuery<T>
{
  public type = "all";
  toString(): string {
    return `(all)`;
  }
  toCoral(): object {
    return {
      combinator: "all",
      subQueries: [],
    };
  }
  getSubQueries() {
    return [];
  }
  toSet(superset: JavaishSet<T>): JavaishSet<T> {
    return superset;
  }
}
type CombinatorArgumentType<T extends Obj> = ActionableQuery<T> &
  ToCoralable<CoralCombinator | CoralValueParentQuery>;
export class CombinatorQuery<T extends Obj>
  implements QueryNode, ActionableQuery<T>, ToCoralable<CoralCombinator>
{
  constructor(
    private ctype: CombinatorTypes,
    private subQueries: AtLeastOne<CombinatorArgumentType<T>>
  ) {}
  public type = CombinatorTypes[this.ctype];
  toString(): string {
    return `(${CombinatorTypes[this.ctype]} ${this.subQueries
      .filter((q) => q !== undefined)
      .map((q) => q.toString())
      .join(" ")})`;
  }
  toCoral(): CoralCombinator {
    return {
      combinator: CombinatorTypes[this.ctype],
      subQueries: this.subQueries.map((q) => q.toCoral()),
    };
  }
  getSubQueries() {
    return this.subQueries;
  }
  toSet(superset: JavaishSet<T>): JavaishSet<T> {
    const childSets = this.subQueries.map((q) => q.toSet(superset)) as any; //oof
    switch (this.ctype) {
      case CombinatorTypes.and:
        return superset.intersect(...childSets);
      case CombinatorTypes.or:
        return childSets[0].union(...childSets);
      case CombinatorTypes.not:
        return superset.subtract(...childSets);
      default:
        throw `unsupported combinator type ${this.ctype}`;
    }
  }
}

export class ValueParentQuery<T extends Obj>
  implements
    QueryNode,
    ActionableQuery<T>,
    ToCoralable<CoralValueParentQuery | CoralCombinator>
{
  constructor(
    private vtype: ValueParentTypes,
    private values: AtLeastOne<ValueQuery>
  ) {}
  public type = ValueParentTypes[this.vtype];
  toString(): string {
    return `(${ValueParentTypes[this.vtype]} ${this.values
      .map((q) => q.toString())
      .join(" ")})`;
  }
  getSubQueries() {
    return [];
  }
  getValues(): AtLeastOne<ValueQuery> {
    return this.values;
  }
  toCoral(): CoralValueParentQuery | CoralCombinator {
    // if its not a range and we have more than one value, we want to split the values
    // and re-combine them as an or query
    if (this.vtype !== ValueParentTypes.range && this.values.length > 1) {
      const childrenRemapped = this.values.map(
        (v) => new ValueParentQuery(this.vtype, [v])
      );
      // 'or' was used before its defined
      return or(childrenRemapped[0], ...childrenRemapped.slice(1)).toCoral(); // eslint-disable-line
    }
    const first = this.values[0].toCoral();
    const valueType = typeof first.val === "object" ? "date" : typeof first.val;
    switch (this.vtype) {
      case ValueParentTypes.fuzzy:
        return {
          attributeName: first.attr,
          queryValue: {
            matchValue: first.val as string,
          },
        };
      case ValueParentTypes.filter:
        return {
          attributeName: first.attr,
          queryValue: {
            filterValue: {
              stringValue: first.val as string,
            },
          },
        };
      case ValueParentTypes.range:
        return {
          attributeName: first.attr,
          queryValue: {
            rangeValue: {
              inclusiveLowerBound: {
                dateValue: first.val as Date,
              },
              inclusiveUpperBound: {
                dateValue: this.values[1].toCoral().val as Date,
              },
            },
          },
        };
      default:
        throw `unsupported value parent type ${this.vtype}`;
    }
  }
  toSet(superset: JavaishSet<T>): JavaishSet<T> {
    switch (this.vtype) {
      case ValueParentTypes.fuzzy:
        return this.matchBy(superset, this.values);
      case ValueParentTypes.filter:
        return this.matchBy(superset, this.values);
      case ValueParentTypes.range:
        return this.rangeBy(superset, this.values);
      default:
        throw `unsupported value parent type ${this.vtype}`;
    }
  }
  matchBy(
    superset: JavaishSet<T>,
    values: AtLeastOne<ValueQuery>
  ): JavaishSet<T> {
    const elements = superset.toArray().filter((element) => {
      for (let i = 0; i < values.length; i++) {
        if (element[values[i].attribute] === values[i].value) {
          return true;
        }
      }
      return false;
    });
    return new JavaishSet<T>(elements);
  }
  rangeBy(
    superset: JavaishSet<T>,
    values: AtLeastOne<ValueQuery>
  ): JavaishSet<T> {
    const lower = values[0];
    const upper = values[1];
    if (upper === undefined) {
      throw "undefined upper range value";
    }
    if (lower.attribute !== upper.attribute) {
      throw "mismatched attributes in range query";
    }

    const elements = superset
      .toArray()
      .filter((element) =>
        (element[lower.attribute] as Date) > new Date(lower.value as any) &&
        (element[upper.attribute] as Date) < new Date(upper.value as any)
          ? true
          : false
      );
    return new JavaishSet<T>(elements);
  }
}

export class ValueQuery implements QueryNode {
  constructor(public attribute: string, public value: PermissableValue) {}
  public type = "val";
  toString(): string {
    return `'${this.attribute}':'${
      typeof this.value === "string" || typeof this.value === "boolean"
        ? this.value
        : this.value.toISOString()
    }'`;
  }
  getSubQueries() {
    return [];
  }
  toCoral(): { attr: string; val: PermissableValue } {
    return this.attribute === "marketplace"
      ? {
          attr: "obfuscatedMarketplaceId",
          val: getMarketplaceId(this.value as string) || this.value,
        }
      : {
          attr: this.attribute,
          val: this.value,
        };
  }
}

type CombinatorBuilder<T extends Obj> = (
  first: ActionableQuery<T>,
  ...rest: ActionableQuery<T>[]
) => CombinatorQuery<T>;
type ValueParentQueryBuilder<T extends Obj> = (
  headValQ: ValueQuery,
  ...tailValQs: ValueQuery[]
) => ValueParentQuery<T>;
type QueryBuilderType<T extends Obj> = {
  val: (attribute: string, value: PermissableValue) => ValueQuery;
  all: () => ActionableQuery<T>;
  and: CombinatorBuilder<T>;
  or: CombinatorBuilder<T>;
  not: CombinatorBuilder<T>;
  fuzzy: ValueParentQueryBuilder<T>;
  filter: ValueParentQueryBuilder<T>;
  range: ValueParentQueryBuilder<T>;
};
export const QueryBuilders: QueryBuilderType<Obj> = {
  all: () => new AllResults(),
  val: (attribute, value) => new ValueQuery(attribute, value),
  ...Object.assign(
    {},
    ...enumKeys(CombinatorTypes).map((ctype) => ({
      [ctype]: <T extends Obj>(
        first: CombinatorArgumentType<T>,
        ...rest: CombinatorArgumentType<T>[]
      ): CombinatorArgumentType<T> =>
        new CombinatorQuery<T>(CombinatorTypes[ctype], [first, ...rest]),
    }))
  ),
  ...Object.assign(
    {},
    ...enumKeys(ValueParentTypes).map((vtype) => ({
      [vtype]: <T extends Obj>(
        headValQ: ValueQuery,
        ...tailValQs: ValueQuery[]
      ): ValueParentQuery<T> =>
        new ValueParentQuery(ValueParentTypes[vtype], [headValQ, ...tailValQs]),
    }))
  ),
};

const { and, or, not, fuzzy, filter, range, val } = QueryBuilders;

function splitLispyString(str: string): string[] {
  const stack = [];
  const strings = [];
  let char, i;
  for (i = 0; i < str.length; i++) {
    char = str[i];
    if (char === "(") {
      stack.push(i);
    }
    if (char === ")") {
      if (stack.length === 1) {
        strings.push(str.slice(stack[0], i + 1));
      }
      stack.pop();
    }
  }
  return strings;
}
const valueTupleMatcher = /'([\w\s])+':'([\w+\s\-.,:\/|])+'/gm; //matches 'attr':'fresh lot' and 'announceDate':'1989-05-31T07:00:00.000Z' and 'attr':'d+p_bundle' and 'attr':'N/A' and 'attr':'DEVICE|PACKOUT'
// This function can parse the strings that are produced by the toString methods on
// the query objects and NOTHING ELSE.  WILD assumptions about input are made
// in order for this to be simple enough to implement here.
export function parseQuery(queryString?: string): QueryType {
  if (queryString === undefined || queryString === "") {
    throw "query string must be provided";
  }

  // basic recursive descent keying off of '(' and ')'
  const str = queryString.slice(1, queryString.length - 1).replace("\n", "");
  const op = str.split(" ")[0];
  if (typeof (QueryBuilders as any)[op] !== "function") {
    throw `unsupported query operation ${op}`;
  }
  // if its a value parent query; this is our recursive base case
  if (enumKeys(ValueParentTypes).filter((k) => k === op).length > 0) {
    const valuesStrings = str.match(valueTupleMatcher) || [];
    return (QueryBuilders as any)[op](
      ...valuesStrings.map((valueString: string) => {
        const valueContents = valueString
          .split("':'")
          .map((s) => s.replace(/'/g, ""));
        if (op === "range") {
          return val(valueContents[0], new Date(valueContents[1]));
        }
        return val(valueContents[0], valueContents[1]);
      })
    );
  }
  //recursive condition takes the top level parenthetical expressions and evaluates them
  const subQueries = splitLispyString(str);
  return (QueryBuilders as any)[op](...subQueries.map(parseQuery));
}

export type SearchResult = Page &
  Sort & { asins: (FullAsinWithAudits | RestrictedAsin)[] };
export type Query = { queryAttributes: QueryType };
export type SearchInput = Page & Sort & Query;

export type SearchAggregationResult = Page &
  Aggregation & {
    aggregationResults?: AggregationResult;
    scalarAggregationResults?: ScalarAggregationResults;
    nestedAggregationResults?: NestedAggregationResults;
  };
export type SearchAggregationInput = Page &
  Query &
  Aggregation & {
    nestedAggregation?: NestedAggregationFacet;
  };
